import { YmdStrToDate } from '@lib/common';
import { ContentDetail, MasterData } from '@lib/content';
import { ContentForm } from './content.form.types';
const mappingFunction = (ids: string, names: string): Object[] => {
  const mappedData = [];
  if (ids) {
    const idsSplitted = ids.split(',');
    const namesSplitted = names?.split(',');
    if (namesSplitted && namesSplitted?.length > 0) {
      namesSplitted.forEach((eachName, idx) =>
        mappedData.push({ id: idsSplitted[idx], name: eachName })
      );
    }
  }
  return mappedData;
};

function pad2(value: number) {
  return ('0' + value).slice(-2);
}

function isValidDate(value: unknown): value is Date {
  return value instanceof Date && !isNaN(value.getTime());
}

function toReleaseDateYmd(release_date?: string | Date): string {
  if (!release_date) return '';

  if (isValidDate(release_date)) {
    return `${release_date.getFullYear()}-${pad2(release_date.getMonth() + 1)}-${pad2(release_date.getDate())}`;
  }

  if (typeof release_date === 'string') {
    const trimmed = release_date.trim();
    if (!trimmed || /nan/i.test(trimmed)) return '';
    if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
      const [dd, mm, yyyy] = trimmed.split('-');
      return dd && mm && yyyy ? `${yyyy}-${mm}-${dd}` : '';
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
  }

  return '';
}

function toForm({
  release_date,
  genres,
  genres_id,
  lang_names,
  language_id,
  origin_country,
  origin_country_name,
  content_keywords,
  poster,
  filming_location,
  season_details=[],
  ...content
}: ContentDetail): ContentForm {
  const genresMapping = mappingFunction(genres_id, genres);
  const languageMapping = mappingFunction(language_id, lang_names);
  const originCountryMapping = mappingFunction(origin_country, origin_country_name);
  const filmingLocation = filming_location as unknown as string;
     
  return {
    ...content,
    season_names: season_details?.map(({name=""})=>name),
    series_filming_end_dates: season_details?.map(({filming_end_date=""})=>filming_end_date),
    series_filming_start_dates: season_details?.map(({filming_start_date=""})=>filming_start_date),
    season_aired_on_dates: season_details?.map(({air_date=""})=>air_date),
    season_numbers: season_details?.map(({season_number=""})=>season_number) as number[],
    season_vtr_id: season_details?.map(({vtr_id=""})=> vtr_id),
    genres_id: genresMapping.length !== 0 ? genresMapping : [],
    language_id: languageMapping.length !== 0 ? languageMapping[0] : {},
    origin_country: originCountryMapping.length !== 0 ? originCountryMapping : [],
    content_keywords: content_keywords ? content_keywords : [],
    release_date: YmdStrToDate(release_date),
    ...(poster && poster !== 'deleted'
      ? { poster: { data: (poster as string).split(',')[1] } }
      : poster === 'deleted'
      ? { poster: { data: null } }
      : null),
    filming_location: filmingLocation ? filmingLocation.split("|"): []
  };
}
function fromForm({
  release_date,
  language_id,
  origin_country,
  genres_id,
  status,
  production_year,
  poster,
  datasource_id,
  media_details,
  content_keywords,
  season_names = [],
  series_filming_end_dates = [],
  series_filming_start_dates = [],
  season_aired_on_dates = [],
  season_numbers = [],
  season_vtr_id = [],
  ...content
}: ContentForm & { release_date?: string | Date }): ContentDetail {
  function checkType(obj) {
    if (typeof obj === 'string') {
      return 'string';
    } else if (typeof obj === 'object') {
      return 'object';
    }
  }

  const seasonDetails = season_numbers?.map((season, idx)=>{
    if(!season) return null;
    return {
      season_number: season,
      name: season_names?.[idx],
      filming_start_date: series_filming_start_dates?.[idx],
      filming_end_date: series_filming_end_dates?.[idx],
      airing_date: season_aired_on_dates[idx],
      vtr_id: season_vtr_id[idx]
    }
  })?.filter((detail)=> detail);

  // Debug log: print main content vtr_id
 
  const castAndCrew = content.cast_crew.filter(x => x && Object.keys(x).length > 0);
  const changedCastAndCrew = castAndCrew.map((x: any, idx) => {
    // Debug log: print each cast/crew meta.vtr_id and name
    if (x?.meta) {
    //  console.log(`[Adapter] Cast/Crew #${idx} meta.vtr_id:`, x.meta.vtr_id, 'name:', x.meta.name);
      return  {
        "person_vtr_id": x?.meta?.id,
        "name": x?.meta?.name,
        "role": x?.role || null,
        "sub_role": x?.sub_role || null,
        "character": x?.character || null,
        "person_tmdb_id": x?.meta?.person_tmdb_id || null,
        "order_id": x?.order_id || null
      }
    }
    return x;
  });

  const release_date_ymd = toReleaseDateYmd(release_date);

  return {
    ...content,
    vote_average: content.vote_average !== null && content.vote_average !== undefined &&
      content.vote_average !== '' && !isNaN(Number(content.vote_average)) ?
      Number(content.vote_average) : null,
    cast_crew: changedCastAndCrew,
    season_details: seasonDetails,
    release_date: release_date_ymd || '',
    release_year: (() => {
      const dateStr = String(release_date_ymd);
      const year = dateStr.slice(0, 4);
      return /^\d{4}$/.test(year) ? year : undefined;
    })(),
    language_id: language_id ? language_id['id'] : null,
    origin_country: origin_country ? origin_country?.map(({ id }) => id)?.join(",") : null,
    genres_id: genres_id ? (genres_id as Array<MasterData>).map(x => x.id).join(',') : null,
    status: checkType(status) === 'string' ? status : checkType(status) === 'object' ? status?.name || null : null,
    production_year: production_year,
    content_keywords: content_keywords ? content_keywords : '',
    media_details: media_details?.map(
      eachMedia => (
        (eachMedia.language_id = eachMedia.language_id.id as unknown as MasterData), eachMedia
      )
    ),
    ...(poster && poster !== 'deleted'
      ? { poster: { data: (poster as string).split(',')[1] } }
      : poster === 'deleted'
        ? { poster: { data: null } }
        : null),
  };
}

function toContentDedupeForm({
  release_date,
  genres,
  genres_id,
  lang_names,
  language_id,
  origin_country,
  origin_country_name,
  content_keywords,
  poster,
  filming_location,
  ...content
}: ContentDetail): ContentForm {
  const genresMapping = mappingFunction(genres_id, genres);
  const languageMapping = mappingFunction(language_id, lang_names);
  const originCountryMapping = mappingFunction(origin_country, origin_country_name);
  const filmingLocation = filming_location as unknown as string;
  return {
    ...content,
    genres_id: genresMapping.length !== 0 ? genresMapping : [],
    language_id: languageMapping.length !== 0 ? languageMapping[0] : {},
    origin_country: (origin_country && typeof origin_country === "string") ? origin_country?.split(",")?.map((id)=>id?.trim()) : [],
    content_keywords: content_keywords ? content_keywords : [],
    release_date: YmdStrToDate(release_date),
    ...(poster && poster !== 'deleted'
      ? { poster: { data: (poster as string).split(',')[1] } }
      : poster === 'deleted'
      ? { poster: { data: null } }
      : null),
    filming_location: filmingLocation ? filmingLocation.split(","): []
  };
}

function fromContentDedupeForm({
  release_date,
  language_id,
  origin_country,
  genres_id,
  status,
  production_year,
  poster,
  datasource_id,
  media_details,
  content_keywords,
  content_id,
  ...content
}: ContentForm): ContentDetail {
  function checkType(obj) {
    if (typeof obj === 'string') {
      return 'string';
    } else if (typeof obj === 'object') {
      return 'object';
    }
  }

  const obj =  {
    ...content,
    release_date: toReleaseDateYmd(release_date) || null,
    origin_country: (origin_country && origin_country?.length > 0) ? origin_country?.filter((id)=>id)?.map((id)=>id?.trim())?.join(",") : "",
    genres:genres_id ? (genres_id as Array<MasterData>).map(x => x.id).join(',') : null,
    genres_id: genres_id ? (genres_id as Array<MasterData>).map(x => x.id).join(',') : null,
    status: checkType(status) === 'string' ? status : checkType(status) === 'object' ? status?.name || null : null,
    production_year: production_year,
    content_keywords: content_keywords ? content_keywords : '',
    media_details: media_details?.map(
      eachMedia => (
        (eachMedia.language_id = eachMedia.language_id.id as unknown as MasterData), eachMedia
      )
    ),
    ...(poster && poster !== 'deleted'
      ? { poster: { data: (poster as string).split(',')[1] } }
      : poster === 'deleted'
      ? { poster: { data: null } }
      : null),
    
      language_id: language_id ? language_id['id'] : null,
    content_id: content_id
  };
  delete obj?.country_names;
  delete obj?.lang_names;
  return obj
}

const ConentFormAdapter = {
  toForm,
  fromForm,
  fromContentDedupeForm,
  toContentDedupeForm
} as const;

export default ConentFormAdapter;